import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import FileUploadAlert from '../projects/components/FileUploadAlert';

const FileUploadContext = createContext();

export const FileUploadProvider = ({ children }) => {
  // Separate state hooks for better readability and performance
  const [files, setFiles] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);

  // File management functions
  const addFiles = useCallback((newFiles) => {
    setFiles(prevFiles => ({
      ...prevFiles,
      ...newFiles
    }));
    setIsModalVisible(true);
  }, []);

  const updateFile = useCallback((fileId, updates) => {
    setFiles(prevFiles => ({
      ...prevFiles,
      [fileId]: {
        ...prevFiles[fileId],
        ...updates
      }
    }));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles({});
    setIsModalVisible(false);
  }, []);

  // Modal management
  const showModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const hideModal = useCallback(() => {
    // When hiding modal, remove successfully loaded files but keep errors and in-progress
    // This matches the original PARSE_FILES_HIDE behavior
    setFiles(prevFiles => {
      const remainingFiles = {};
      Object.entries(prevFiles).forEach(([id, file]) => {
        if (!file.loaded) {
          remainingFiles[id] = file;
        }
      });
      return remainingFiles;
    });
    setIsModalVisible(false);
  }, []);

  const dismissFile = useCallback((dismiss) => {
    if (dismiss) {
      hideModal();
    } else {
      showModal();
    }
  }, [hideModal, showModal]);

  // Computed values
  const filesArray = useMemo(() => {
    return Object.values(files).filter(file => !!file);
  }, [files]);

  const fileStatus = useMemo(() => {
    if (filesArray.length === 0) return null;

    const errorFiles = filesArray.filter(file => !!file.error);
    const loadedFiles = filesArray.filter(file => !!file.loaded);
    const totalFiles = filesArray.length;
    const isLoading = totalFiles !== (errorFiles.length + loadedFiles.length);

    // Helper for pluralization
    const pluralize = (count, word) => `${count} ${word}${count !== 1 ? 's' : ''}`;

    // Build status messages
    let message = '';
    let errorMessage = null;
    const isDone = !isLoading;

    if (errorFiles.length > 0) {
      errorMessage = `Issues importing ${pluralize(errorFiles.length, 'file')}`;
    }

    if (isDone) {
      message = `${pluralize(loadedFiles.length, 'file')} successfully imported`;
    } else {
      message = `Importing files: ${loadedFiles.length}/${totalFiles}`;
    }

    return {
      hasError: errorFiles.length > 0,
      isDone,
      isLoading,
      message,
      errorMessage,
      errorCount: errorFiles.length,
      loadedCount: loadedFiles.length,
      totalCount: totalFiles
    };
  }, [filesArray]);

  // Check if there are files with specific error types
  const hasTooBigError = useMemo(() => {
    return filesArray.some(file => file.error?.type === 'FILES__TOO_BIG');
  }, [filesArray]);

  const value = {
    // State
    files,
    filesArray,
    isModalVisible,
    fileStatus,
    hasTooBigError,
    
    // Actions
    addFiles,
    updateFile,
    clearFiles,
    showModal,
    hideModal,
    dismissFile,
    
    // Direct setters if needed for sync
    setFiles,
    setIsModalVisible
  };

  return (
    <FileUploadContext.Provider value={value}>
      <FileUploadAlert />
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUpload = () => {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error('useFileUpload must be used within FileUploadProvider');
  }
  return context;
};